import { Component, Input, OnInit } from '@angular/core'
import { BlockDataService } from 'src/app/block-services/block-data.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MixpanelService } from 'src/app/tracking/mixpanel.service'
import { CdkDragDrop } from '@angular/cdk/drag-drop'
import { SideMenuItemsService } from './side-menu-items.service'

/**
 * Side Menu block
 *
 * Edits the app side menu (drawer) for the whole project: one design and one list of items
 * for all pages. Nothing is added to the page; changes save right away to general_settings
 * (see SideMenuItemsService) and the app reads them from there.
 */
@Component({
  selector: 'block-sidemenu',
  templateUrl: './block-sidemenu.component.html',
  styleUrls: ['./block-sidemenu.component.scss']
})
export class BlockSidemenuComponent implements OnInit {
  @Input() builderType: 'mobile' | 'website' = 'mobile'
  /** Shown inside Global Settings: open straight on the settings (no blocks-list card) */
  @Input() embedded = false

  readonly defaultItemIcon = 'assets/images/blocks/sidebar-item-icon.svg'

  blockId = ''
  previewFields = false
  styleFields = false

  /** Color fields shown in the Style tab: [style key, label] */
  readonly colorFields = [
    { key: 'backgroundColor', label: 'Background Color' },
    { key: 'cardColor', label: 'Item Background' },
    { key: 'cardBorderColor', label: 'Item Border Color' },
    { key: 'iconBoxColor', label: 'Icon Box Color' },
    { key: 'textColor', label: 'Text Color' },
    { key: 'mutedTextColor', label: 'Secondary Text Color' },
    { key: 'accentColor', label: 'Accent Color' }
  ]

  /** Show / hide toggles shown in the Data tab: [data key, label] */
  readonly visibilityFields = [
    { key: 'showLogo', label: 'Show Logo' },
    { key: 'showTitle', label: 'Show Title & Subtitle' },
    { key: 'showSocial', label: 'Show Social Links' },
    { key: 'showUserFooter', label: 'Show Footer (User / Login)' },
    { key: 'showDecoration', label: 'Show Corner Decoration' }
  ]

  constructor(
    private blockDataService: BlockDataService,
    public builderService: BuilderService,
    private mixPanel: MixpanelService,
    public menuItems: SideMenuItemsService
  ) {}

  ngOnInit() {
    if (this.embedded) this.showDataTab()
    this.menuItems.load()
    this.builderService.showSettingsProgress = false
  }

  /** Clicking the block in the blocks list opens its settings (nothing is added to the page) */
  openSettings() {
    this.blockDataService.sendCurrentBlock(BlockSidemenuComponent)
    this.mixPanel.track('Side Menu Opened', {
      projectid: this.builderService.getSelectedProject()
    })
  }

  getBlockFields(_id?: string) {
    return this.menuItems.design
  }

  /** Read a data value; show/hide flags default to true when missing */
  dataValue(key: string) {
    const value = this.getBlockFields(this.blockId)?.data?.[key]
    if (key.startsWith('show')) return value !== false
    return value
  }

  styleValue(key: string, fallback = '') {
    return this.getBlockFields(this.blockId)?.style?.[key] || fallback
  }

  /** Default colors used when a style value is empty (accent follows the project primary color) */
  colorFallback(key: string) {
    const defaults: Record<string, string> = {
      backgroundColor: '#ffffff',
      cardColor: '#ffffff',
      cardBorderColor: '#c3c6ce',
      iconBoxColor: '#f2f4f6',
      textColor: '#191c1e',
      mutedTextColor: '#43474d',
      accentColor: getComputedStyle(document.documentElement).getPropertyValue('--ion-color-primary').trim() || '#567edc'
    }
    return defaults[key] || '#000000'
  }

  dataChange(key: string, _blockId: any, $event: any, type: 'text' | 'toggle') {
    const value = type === 'toggle' ? $event.checked : $event.currentTarget.value
    this.menuItems.setData(key, value)
  }

  styleChange(key: string, _blockId: any, value: any) {
    this.menuItems.setStyle(key, value)
  }

  colorChange(key: string, blockId: any, $event: any) {
    this.styleChange(key, blockId, $event.currentTarget.value)
  }

  /** Empty value = use the app default again */
  resetColor(key: string, blockId: any) {
    this.styleChange(key, blockId, '')
  }

  widthChange(blockId: any, $event: any) {
    const raw = $event.value ?? $event.currentTarget?.value
    this.styleChange('maxWidth', blockId, Number(raw) || 450)
  }

  /** Upload an image (menu item icon or logo) and save its link in the design data */
  processImage(fileInput: any, field: 'itemIcon' | 'logo') {
    const file: File = fileInput.target.files[0]
    if (!file) return
    this.builderService.showSettingsProgress = true
    const done = (src: any) => {
      this.builderService.showSettingsProgress = false
      this.menuItems.setData(field, src)
    }
    const failed = () => {
      this.builderService.showSettingsProgress = false
      alert('error uploading file, please contact support')
    }
    if (this.builderService.standalone) {
      this.builderService.uploadImage(file, this.builderService.selectedProject, 'custom-images')?.subscribe(done, failed)
    } else {
      this.builderService.uploadFileCustomerServer(file, this.builderService.selectedProject, '').subscribe(done, failed)
    }
  }

  removeImage(field: 'itemIcon' | 'logo') {
    this.menuItems.setData(field, '')
  }

  /** CSS variables for the preview drawer (empty values fall back to the defaults in the SCSS) */
  previewVars(): Record<string, string> {
    const vars: Record<string, string> = {}
    const map: Record<string, string> = {
      backgroundColor: '--sm-bg',
      cardColor: '--sm-card',
      cardBorderColor: '--sm-border',
      iconBoxColor: '--sm-icon-box',
      textColor: '--sm-text',
      mutedTextColor: '--sm-muted',
      accentColor: '--sm-accent'
    }
    for (const key in map) {
      const value = this.styleValue(key)
      if (value) vars[map[key]] = value
    }
    return vars
  }

  /** Items for the preview: the project menu items, otherwise placeholders */
  get previewItems(): string[] {
    if (this.menuItems.items.length) {
      return this.menuItems.items.map((item) => item?.name || '')
    }
    return ['Home', 'Categories', 'My Account', 'Contact Us']
  }

  /*** Menu items (shared by all pages, saved to general_settings.menus) ***/
  newItemName = ''
  newItemLink = 'home'

  addMenuItem() {
    this.menuItems.add(this.newItemName, this.newItemLink)
    this.newItemName = ''
    this.newItemLink = 'home'
  }

  menuItemDrop(event: CdkDragDrop<any>) {
    this.menuItems.move(event.previousIndex, event.currentIndex)
  }

  checkSidebar() {
    return !this.previewFields && !this.styleFields
  }

  checkData() {
    return this.previewFields
  }

  checkStyle() {
    return this.styleFields && !this.previewFields
  }

  checkSettings() {
    return !this.checkSidebar()
  }

  showDataTab() {
    this.previewFields = true
    this.styleFields = false
  }

  showStyleTab() {
    this.previewFields = false
    this.styleFields = true
  }
}
